/**
 * supernovaXp — the Supernova staking XP model: every parameter in one place,
 * plus the pure functions that turn a stake lot into XP figures.
 *
 * No React, no I/O, no formatting. Callers (the Supernova tab, its tests) get
 * exact numbers out of here and decide how to display them.
 *
 * PARAMETERS ARE LOCKED AT THE HANDOFF VALUES (SUPERNOVA_HANDOFF.md, decision
 * log 2026-07-24) and live here as config so nothing hard-codes them inline:
 *   - base rate      85 × 1960.78431 / 3600 XP per NFT per day
 *   - lock range     30 days minimum → 3 years (1095 days) maximum
 *   - multipliers    30d 1.0× · 90d 1.1× · 180d 1.25× · 1y 1.5× · 2y 1.75× · 3y 2.0×
 *   - P_MAX          0.75 (75%), the maximum early-exit decay penalty
 *
 * THE MODEL (SUPERNOVA_ENGINE_DESIGN.md §5.5 — this module is the client-side
 * mirror of it, display only; the engine remains the source of truth):
 *   1. XP accrues DAILY PRO-RATA. Nothing is credited up front.
 *   2. Accrual stops at maturity — a matured lot is capped at xpAtMaturity.
 *   3. Early exit is the strictest variant, three stacked losses:
 *        a. unearned XP was never accrued in the first place;
 *        b. the lot is RE-RATED to the longest tier actually served;
 *        c. a linear decay penalty applies:
 *           kept = rerated × (1 − P_MAX × daysRemaining / lockDays)
 *      The penalty denominator is the ORIGINAL lockDays, never the served tier.
 *
 * COMPLIANCE: XP is an in-app points balance. Nothing in this module is a
 * return, a yield, a price, or a projection, and none of it may be presented as
 * one. `xpAtMaturity` is the model's maximum for a lot held to term, not a
 * forecast — label it as such wherever it is shown.
 *
 * Display-only figures (tier bonuses) are marked as such and are NOT credited.
 */

/* ── Base rate ──────────────────────────────────────────────────────────────
   85 × 1960.78431 / 3600 is not a round number and must never be hard-coded as
   46.2963. Kept as an exact reduced fraction so the arithmetic below can be
   done in one division instead of accumulating rounding:
     85 × 1960.78431 / 3600 = 16,666,666,635 / 360,000,000
                            = 1,111,111,109 / 24,000,000
                            ≈ 46.296296208333… XP / NFT / day               */
export const XP_RATE_NUMERATOR = 1111111109;
export const XP_RATE_DENOMINATOR = 24000000;
export const XP_RATE_EXACT = "1111111109/24000000";
/**
 * The base rate written the way the spec states it, for the one place a member
 * is shown the formula (the stake preview). A display string of the SAME locked
 * parameter as the fraction above — if one changes, change both.
 */
export const XP_RATE_FORMULA = "85 × 1960.78431 ÷ 3600";
export const XP_PER_NFT_DAY = XP_RATE_NUMERATOR / XP_RATE_DENOMINATOR;

/* ── Lock durations and their multipliers ───────────────────────────────── */
export const MIN_LOCK_DAYS = 30;
export const MAX_LOCK_DAYS = 1095; // 3 years

/** The six presets a member can choose from (handoff, 2026-07-24). */
export const LOCK_PRESETS = [
  { days: 30, label: "30 days", multiplier: 1.0 },
  { days: 90, label: "90 days", multiplier: 1.1 },
  { days: 180, label: "180 days", multiplier: 1.25 },
  { days: 365, label: "1 year", multiplier: 1.5 },
  { days: 730, label: "2 years", multiplier: 1.75 },
  { days: 1095, label: "3 years", multiplier: 2.0 },
];

/* Tier lookup, longest first. `hundredths` is the integer form used by the
   arithmetic (100/110/125/150/175/200) so multiplication stays exact.
   Anything below the 30-day floor rates at 1.0× — that case only arises on an
   early exit whose SERVED time fell short of the first tier. */
export const MULTIPLIER_TIERS = [
  { minDays: 1095, multiplier: 2.0, hundredths: 200 },
  { minDays: 730, multiplier: 1.75, hundredths: 175 },
  { minDays: 365, multiplier: 1.5, hundredths: 150 },
  { minDays: 180, multiplier: 1.25, hundredths: 125 },
  { minDays: 90, multiplier: 1.1, hundredths: 110 },
  { minDays: 30, multiplier: 1.0, hundredths: 100 },
];
const BASE_TIER = { minDays: 0, multiplier: 1.0, hundredths: 100 };

/* ── Early-exit penalty ─────────────────────────────────────────────────── */
/** Maximum decay penalty, as a fraction. Basis points is the exact form. */
export const P_MAX = 0.75;
export const P_MAX_BPS = 7500;

/* ── Display-only tier bonuses ──────────────────────────────────────────────
   3/5/8/10 staked → 250k/500k/750k/1M XP. Per SUPERNOVA_ENGINE_DESIGN.md §5.5
   these are gated on an unresolved token-valuation minimum (OQ-4) and are NOT
   credited to any balance. Show them as a progress track, explicitly labelled
   "not yet credited". Never add them to earned XP.                          */
export const TIER_BONUSES = [
  { stakedCount: 3, bonusXp: 250000 },
  { stakedCount: 5, bonusXp: 500000 },
  { stakedCount: 8, bonusXp: 750000 },
  { stakedCount: 10, bonusXp: 1000000 },
];
export const TIER_BONUSES_CREDITED = false;

/* ── Copy the UI must not paraphrase ────────────────────────────────────── */
export const XP_DISCLAIMER =
  "XP is an in-app points balance with no monetary value.";
export const STAKE_RISK_DISCLOSURE =
  "Staking locks your NFTs in a third-party smart contract for the full lock " +
  "period. You sign every transaction from your own wallet — ARC Educational never " +
  "holds your keys or your NFTs, and cannot move or return them for you. " +
  "Smart contracts can fail or be exploited. Educational tool, not financial advice.";
export const EARLY_EXIT_DISCLOSURE =
  "Exiting before the lock ends re-rates the stake to the longest tier you " +
  "actually served and then applies a decay penalty, so the XP you keep is " +
  "lower than the figures shown for the full term. This cannot be reversed.";

export const MS_PER_DAY = 86400000;

/* ── Internals ──────────────────────────────────────────────────────────── */

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/** now may be an epoch-ms number or a Date. Anything else is a caller bug. */
function toMs(now, label = "now") {
  const ms = now instanceof Date ? now.getTime() : now;
  if (!isFiniteNumber(ms)) {
    throw new TypeError(`supernovaXp: ${label} must be epoch ms or a Date`);
  }
  return ms;
}

/**
 * Validate and normalize a lot. Throws rather than coercing: a malformed lot
 * means we don't know the member's position, and rendering 0 XP for an unknown
 * position is exactly the "zeros-as-data" failure this build forbids.
 *
 * @typedef {Object} StakeLot
 * @property {number} qty        NFTs in the lot (positive integer)
 * @property {number} lockDays   contracted lock, in days
 * @property {number} stakedAtMs epoch ms the lot opened
 * @property {number} [unlockAtMs] optional; derived when absent
 */
export function normalizeLot(lot) {
  if (!lot || typeof lot !== "object") {
    throw new TypeError("supernovaXp: lot must be an object");
  }
  const { qty, lockDays, stakedAtMs } = lot;
  if (!isFiniteNumber(qty) || qty <= 0 || !Number.isInteger(qty)) {
    throw new TypeError("supernovaXp: lot.qty must be a positive integer");
  }
  if (!isFiniteNumber(lockDays) || lockDays <= 0) {
    throw new TypeError("supernovaXp: lot.lockDays must be a positive number");
  }
  if (!isFiniteNumber(stakedAtMs)) {
    throw new TypeError("supernovaXp: lot.stakedAtMs must be epoch ms");
  }
  const unlockAtMs = isFiniteNumber(lot.unlockAtMs)
    ? lot.unlockAtMs
    : stakedAtMs + lockDays * MS_PER_DAY;
  return { qty, lockDays, stakedAtMs, unlockAtMs };
}

/**
 * The multiplier tier for a whole number of days.
 * Tier lookup uses WHOLE DAYS COMPLETED — floor(daysServed) — so a lot 89.9
 * days in is still on the 30-day tier.
 */
export function multiplierForDays(days) {
  if (!isFiniteNumber(days)) {
    throw new TypeError("supernovaXp: days must be a number");
  }
  const whole = Math.floor(days);
  return (MULTIPLIER_TIERS.find((t) => whole >= t.minDays) || BASE_TIER).multiplier;
}

function tierForDays(days) {
  const whole = Math.floor(days);
  return MULTIPLIER_TIERS.find((t) => whole >= t.minDays) || BASE_TIER;
}

/**
 * qty × days × rate × multiplier, in one division so nothing compounds.
 * `hundredths` is the integer multiplier (100 = 1.00×).
 */
function xpFor(qty, days, hundredths) {
  return (
    (qty * days * hundredths * XP_RATE_NUMERATOR) / (XP_RATE_DENOMINATOR * 100)
  );
}

/**
 * Days served, continuous (seconds pro-rata), clamped to [0, lockDays].
 * Clamping at lockDays is what stops accrual at maturity.
 */
export function daysServed(lot, now) {
  const { lockDays, stakedAtMs } = normalizeLot(lot);
  const elapsed = (toMs(now) - stakedAtMs) / MS_PER_DAY;
  if (elapsed <= 0) return 0;
  return Math.min(elapsed, lockDays);
}

/** True once the lot has reached its unlock time. */
export function isMatured(lot, now) {
  const { unlockAtMs } = normalizeLot(lot);
  return toMs(now) >= unlockAtMs;
}

/**
 * The model's maximum for a lot held to term at its contracted multiplier.
 * NOT a projection or a promise — a ceiling under the current parameters.
 */
export function xpAtMaturity(lot) {
  const { qty, lockDays } = normalizeLot(lot);
  return xpFor(qty, lockDays, tierForDays(lockDays).hundredths);
}

/**
 * XP accrued so far at the lot's CONTRACTED multiplier — what the member has
 * if they hold to term. Daily pro-rata, capped at maturity.
 *
 * This is the headline "earned" figure. It is not what an early exit pays out;
 * for that, see exitQuote().
 *
 * @param {StakeLot} lot
 * @param {number|Date} now epoch ms
 * @returns {number} XP, unrounded
 */
export function earnedXP(lot, now) {
  const normalized = normalizeLot(lot);
  const served = daysServed(normalized, now);
  return xpFor(normalized.qty, served, tierForDays(normalized.lockDays).hundredths);
}

/**
 * What an exit RIGHT NOW would actually pay, line by line.
 *
 * At or after maturity there is nothing to forfeit: kept === earned.
 * Before maturity, the three stacked losses of §5.5 apply.
 *
 * @param {StakeLot} lot
 * @param {number|Date} now epoch ms
 * @returns {{
 *   earned: number,        // accrued at the CONTRACTED multiplier
 *   rerated: number,       // re-rated to the tier actually served
 *   penaltyPct: number,    // decay penalty, 0–75, as a percentage
 *   kept: number,          // what the member walks away with
 *   servedDays: number,
 *   servedTierMultiplier: number,
 *   rerateLossXp: number,  // earned − rerated
 *   penaltyXp: number,     // rerated − kept
 *   matured: boolean,
 *   quotedAtMs: number
 * }}
 */
export function exitQuote(lot, now) {
  const normalized = normalizeLot(lot);
  const quotedAtMs = toMs(now);
  const { qty, lockDays } = normalized;
  const served = daysServed(normalized, quotedAtMs);
  const earned = xpFor(qty, served, tierForDays(lockDays).hundredths);

  if (isMatured(normalized, quotedAtMs)) {
    return {
      earned,
      rerated: earned,
      penaltyPct: 0,
      kept: earned,
      servedDays: served,
      servedTierMultiplier: tierForDays(lockDays).multiplier,
      rerateLossXp: 0,
      penaltyXp: 0,
      matured: true,
      quotedAtMs,
    };
  }

  // 1. Re-rate to the longest tier actually SERVED (whole days completed).
  const servedTier = tierForDays(served);
  const rerated = xpFor(qty, served, servedTier.hundredths);

  // 2. Linear decay penalty. Denominator is the ORIGINAL lockDays.
  const daysRemaining = lockDays - served;
  const penaltyFraction = (P_MAX_BPS * daysRemaining) / (10000 * lockDays);
  const kept = Math.max(0, rerated * (1 - penaltyFraction));

  return {
    earned,
    rerated,
    penaltyPct: penaltyFraction * 100,
    kept,
    servedDays: served,
    servedTierMultiplier: servedTier.multiplier,
    rerateLossXp: earned - rerated,
    penaltyXp: rerated - kept,
    matured: false,
    quotedAtMs,
  };
}

/**
 * Half-up rounding for display, 6 dp by default — the same rounding the engine
 * applies before it serves XP over the API (§5.4), so the tab and the engine
 * agree digit for digit.
 */
export function roundXp(xp, decimals = 6) {
  if (!isFiniteNumber(xp)) {
    throw new TypeError("supernovaXp: roundXp expects a finite number");
  }
  const factor = 10 ** decimals;
  return Math.sign(xp) * (Math.round(Math.abs(xp) * factor) / factor);
}
